/**
 * Staff Calendar Types for Multi-Staff Calendar Views
 *
 * These types support the multi-staff calendar visualization in the operator dashboard,
 * providing type-safe interfaces for staff-aware calendar views, availability management,
 * and multi-column/overlay display modes.
 */

import type { BookingStatus } from './calendar';

// ============================================================================
// Staff Member Types
// ============================================================================

/**
 * Staff member data structured for calendar display
 */
export interface CalendarStaffMember {
  /** Unique staff identifier */
  id: string;

  /** Staff member's display name */
  name: string;

  /** Color assigned to this staff member for visual distinction */
  color: string;

  /** Optional avatar image URL */
  avatarUrl?: string;

  /** Staff member's role/position */
  role: string;

  /** Whether the staff member is currently available for bookings */
  isAvailable: boolean;

  /** Count of bookings for the current day */
  todayBookingsCount: number;
}

// ============================================================================
// Staff Calendar Event Types
// ============================================================================

/**
 * Calendar event with staff-specific information for multi-staff views
 */
export interface StaffCalendarEvent {
  /** Unique event identifier */
  id: string;

  /** Event title/display name */
  title: string;

  /** Event start time */
  start: Date;

  /** Event end time */
  end: Date;

  /** Assigned staff member ID (optional for unassigned bookings) */
  staffId?: string;

  /** Assigned staff member name for display */
  staffName?: string;

  /** Staff member's assigned color for visual consistency */
  staffColor?: string;

  /** Service identifier */
  serviceId: string;

  /** Service name for display */
  serviceName: string;

  /** Customer identifier */
  customerId: string;

  /** Customer name for display */
  customerName: string;

  /** Booking status */
  status: BookingStatus;

  /** Background color for the event block */
  backgroundColor: string;

  /** Border color for the event block */
  borderColor: string;

  /** Text color for the event block */
  textColor: string;
}

// ============================================================================
// Availability Block Types
// ============================================================================

/**
 * Type of availability block
 */
export type AvailabilityBlockType = 'unavailable' | 'break' | 'holiday' | 'external';

/**
 * Availability block representing non-bookable time periods
 * Used for breaks, days off, holidays, and external commitments
 */
export interface AvailabilityBlock {
  /** Unique block identifier */
  id: string;

  /** Staff member this block applies to */
  staffId: string;

  /** Staff member name for display */
  staffName: string;

  /** Block start time */
  start: Date;

  /** Block end time */
  end: Date;

  /** Type of availability block */
  type: AvailabilityBlockType;

  /** Display title for the block */
  title: string;

  /** Display mode - always 'background' for availability blocks */
  display: 'background';

  /** Background color for the block visualization */
  backgroundColor: string;
}

// ============================================================================
// Calendar View Mode Types
// ============================================================================

/**
 * Available view modes for multi-staff calendar
 *
 * - single: Traditional single-column view (one staff or all combined)
 * - multi-column: Side-by-side columns for each selected staff member
 * - overlay: All staff events overlaid on a single timeline with color coding
 * - timeline: Horizontal timeline with staff as rows
 */
export type CalendarViewMode = 'single' | 'multi-column' | 'overlay' | 'timeline';

/**
 * Labels for calendar view modes
 */
export const CALENDAR_VIEW_MODE_LABELS: Record<CalendarViewMode, string> = {
  single: 'Single View',
  'multi-column': 'Multi-Column',
  overlay: 'Overlay',
  timeline: 'Timeline',
};

/**
 * Icons/descriptions for calendar view modes
 */
export const CALENDAR_VIEW_MODE_DESCRIPTIONS: Record<CalendarViewMode, string> = {
  single: 'View one staff member or all bookings combined',
  'multi-column': 'Side-by-side columns for each staff member',
  overlay: 'All staff overlaid with color-coded events',
  timeline: 'Horizontal timeline with staff as rows',
};

// ============================================================================
// Staff Filter Types
// ============================================================================

/**
 * State for staff filtering in the calendar
 */
export interface StaffFilterState {
  /** Array of selected staff member IDs */
  selectedIds: string[];

  /** Whether to show all staff members */
  showAll: boolean;

  /** Whether to include availability blocks in the view */
  includeAvailability: boolean;
}

/**
 * Default staff filter state
 */
export const DEFAULT_STAFF_FILTER_STATE: StaffFilterState = {
  selectedIds: [],
  showAll: true,
  includeAvailability: true,
};

// ============================================================================
// Color Constants for Availability Blocks
// ============================================================================

/**
 * Color configuration for availability block types
 */
export const AVAILABILITY_BLOCK_COLORS: Record<AvailabilityBlockType, {
  bg: string;
  hex: string;
  label: string;
}> = {
  unavailable: {
    bg: 'bg-slate-200',
    hex: '#e2e8f0',
    label: 'Unavailable',
  },
  break: {
    bg: 'bg-amber-100',
    hex: '#fef3c7',
    label: 'Break',
  },
  holiday: {
    bg: 'bg-sky-100',
    hex: '#e0f2fe',
    label: 'Holiday',
  },
  external: {
    bg: 'bg-violet-100',
    hex: '#ede9fe',
    label: 'External',
  },
};

// ============================================================================
// Staff Calendar Configuration
// ============================================================================

/**
 * Configuration options for staff calendar display
 */
export interface StaffCalendarConfig {
  /** Current view mode */
  viewMode: CalendarViewMode;

  /** Staff filter state */
  staffFilter: StaffFilterState;

  /** Whether to show unassigned bookings */
  showUnassigned: boolean;

  /** Minimum column width in pixels for multi-column view */
  minColumnWidth: number;

  /** Maximum number of columns to display in multi-column view */
  maxColumns: number;

  /** Whether to auto-scroll to current time */
  autoScrollToNow: boolean;
}

/**
 * Default staff calendar configuration
 */
export const DEFAULT_STAFF_CALENDAR_CONFIG: StaffCalendarConfig = {
  viewMode: 'single',
  staffFilter: DEFAULT_STAFF_FILTER_STATE,
  showUnassigned: true,
  minColumnWidth: 200,
  maxColumns: 6,
  autoScrollToNow: true,
};

// ============================================================================
// Staff Calendar Event Handlers
// ============================================================================

/**
 * Event handlers specific to staff calendar interactions
 */
export interface StaffCalendarEventHandlers {
  /** Called when a staff member is selected/deselected */
  onStaffSelect?: (staffId: string, selected: boolean) => void;

  /** Called when the view mode changes */
  onViewModeChange?: (mode: CalendarViewMode) => void;

  /** Called when an availability block is clicked */
  onAvailabilityClick?: (block: AvailabilityBlock) => void;

  /** Called when creating a new availability block */
  onCreateAvailability?: (staffId: string, start: Date, end: Date) => void;

  /** Called when an event is assigned to a different staff member */
  onEventReassign?: (eventId: string, newStaffId: string) => void;
}

// ============================================================================
// Utility Types
// ============================================================================

/**
 * Events grouped by staff member ID
 */
export interface EventsByStaff {
  [staffId: string]: StaffCalendarEvent[];
}

/**
 * Availability blocks grouped by staff member ID
 */
export interface AvailabilityByStaff {
  [staffId: string]: AvailabilityBlock[];
}

/**
 * Staff calendar data aggregate for a time period
 */
export interface StaffCalendarData {
  /** Staff members to display */
  staff: CalendarStaffMember[];

  /** All calendar events */
  events: StaffCalendarEvent[];

  /** Events grouped by staff */
  eventsByStaff: EventsByStaff;

  /** All availability blocks */
  availability: AvailabilityBlock[];

  /** Availability grouped by staff */
  availabilityByStaff: AvailabilityByStaff;

  /** Unassigned events (no staffId) */
  unassignedEvents: StaffCalendarEvent[];
}

/**
 * Staff utilization metrics for a time period
 */
export interface StaffUtilization {
  /** Staff member ID */
  staffId: string;

  /** Staff member name */
  staffName: string;

  /** Total available minutes in the period */
  availableMinutes: number;

  /** Total booked minutes */
  bookedMinutes: number;

  /** Utilization percentage (0-100) */
  utilizationPercent: number;

  /** Number of bookings */
  bookingCount: number;

  /** Revenue generated (if applicable) */
  revenue?: number;
}
